import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Instagram, Facebook, Twitter, Youtube, 
  Mail, Phone, MapPin, ArrowRight, Play, ChevronDown,
  Calendar, Clock, Users, Sparkles, Send, Share2, Copy, MessageCircle, Check
} from "lucide-react";

// Admin imports
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { 
  LoginPage, AdminLayout, DashboardPage, 
  NewsPage, GalleryPage, ArtistsPage, ContactsPage 
} from "./pages/admin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to resolve image URLs
const resolveImageUrl = (url) => {
  if (!url) return '';
  // If it's already an absolute URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it's a relative URL starting with /api/, prepend BACKEND_URL
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  // Otherwise return as is
  return url;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Navigation Component
const Navigation = ({ activeSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Início", href: "#inicio" },
    { name: "Sobre", href: "#sobre" },
    { name: "Serviços", href: "#servicos" },
    { name: "Artistas", href: "#artistas" },
    { name: "Notícias", href: "#noticias" },
    { name: "Contato", href: "#contato" },
    { name: "Entrar", href: "/admin", isAdmin: true },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-glass py-4" : "bg-transparent py-6"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <a href="#inicio" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_event-puramania/artifacts/agcwure7_logo%20marca%202.png" 
              alt="Puramania Logo" 
              className="h-12 w-auto"
            />
          </a>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              item.isAdmin ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-sm font-medium text-[#00F0FF] hover:text-white transition-colors relative group flex items-center gap-1"
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                    <polyline points="10 17 15 12 10 7"/>
                    <line x1="15" y1="12" x2="3" y2="12"/>
                  </svg>
                  {item.name}
                </Link>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-sm font-medium text-gray-300 hover:text-white transition-colors relative group"
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00F0FF] transition-all duration-300 group-hover:w-full" />
                </a>
              )
            ))}
            <a
              href="#contato"
              className="btn-primary text-sm py-3 px-6"
              data-testid="nav-cta"
            >
              Solicitar Orçamento
            </a>
            {/* WhatsApp Button */}
            <a
              href="https://wa.me/5571999999999?text=Olá! Gostaria de mais informações sobre os serviços da Puramania."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5C] text-white text-sm py-3 px-4 rounded-full transition-all duration-300"
              data-testid="nav-whatsapp"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0a0a0a] border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navItems.map((item) => (
                item.isAdmin ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block text-lg font-medium text-[#00F0FF] hover:text-white flex items-center gap-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                      <polyline points="10 17 15 12 10 7"/>
                      <line x1="15" y1="12" x2="3" y2="12"/>
                    </svg>
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block text-lg font-medium text-gray-300 hover:text-white"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </a>
                )
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="inicio" className="hero-section" data-testid="hero-section">
      <div
        className="hero-bg"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920&q=80')`,
        }}
      />
      <div className="hero-overlay" />
      
      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-block text-[#00F0FF] text-sm font-mono tracking-[0.3em] uppercase mb-6">
            Produções & Eventos
          </span>
        </motion.div>
        
        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-unbounded text-5xl md:text-7xl lg:text-9xl font-black tracking-tighter uppercase mb-8"
          data-testid="hero-title"
        >
          <span className="gradient-text">PURAMANIA</span>
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-12 leading-relaxed"
          data-testid="hero-description"
        >
          Excelência em Produção e Eventos<br/>
          Shows, festas, Estrutura e criatividade
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a href="#artistas" className="btn-primary" data-testid="hero-cta-artists">
            Conheça Nossos Artistas
          </a>
          <a href="#sobre" className="btn-secondary" data-testid="hero-cta-about">
            Saiba Mais
          </a>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ChevronDown className="text-white/50" size={32} />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Marquee Section
const MarqueeSection = () => {
  const items = [
    "SHOWS", "FESTIVAIS", "EVENTOS CORPORATIVOS", "CARNAVAL", 
    "RÉVEILLON", "FORMATURAS", "CASAMENTOS"
  ];

  return (
    <div className="bg-[#00F0FF] py-5 overflow-hidden" data-testid="marquee-section">
      <div className="marquee-container">
        <div className="marquee-content">
          {[...items, ...items].map((item, index) => (
            <span
              key={index}
              className="text-black font-unbounded font-black text-xl uppercase px-8 flex items-center gap-4"
            >
              {item}
              <Sparkles size={16} />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="sobre" className="py-24 md:py-32 bg-[#050505]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#FF0099] text-sm font-mono tracking-[0.3em] uppercase mb-4 block">
              Quem Somos
            </span>
            <h2 className="section-title mb-8" data-testid="about-title">
              SOBRE <span className="gradient-text">NÓS</span>
            </h2>
            <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
              <p className="text-xl font-medium text-white">
                Conectando Talentos, Realizando Espetáculos, Criando Momentos Inesquecíveis.
              </p>
              <p>
                Nascemos da paixão pela música e pela cultura da Bahia. Com sede em <strong className="text-white">Lauro de Freitas/BA</strong>, 
                a <strong className="text-[#00F0FF]">Puramania Produções e Eventos LTDA</strong> se consolidou como uma força motriz 
                no cenário do entretenimento no Nordeste. Somos mais que uma produtora: somos curadores de experiências, 
                especialistas em transformar eventos em acontecimentos memoráveis que marcam a vida das pessoas.
              </p>
              <p>
                Nosso compromisso é com a <strong className="text-white">excelência</strong>. Entendemos as complexidades da produção 
                de shows e eventos de grande porte, oferecendo uma <strong className="text-[#FF0099]">gestão 360º</strong> – da curadoria 
                artística à logística de palco, som e luz – com soluções personalizadas que garantem segurança, 
                pontualidade e sucesso de público.
              </p>
              <p>
                Para <strong className="text-white">prefeituras e empresas</strong>, somos o parceiro estratégico que assegura 
                a entrega de uma experiência cultural rica e profissional, impulsionando a alegria e a economia local. 
                Nossa equipe dedicada transforma sua visão em realidade, superando expectativas e construindo um 
                legado de eventos inesquecíveis por todo o Brasil.
              </p>
              <p className="text-[#00F0FF] font-medium">
                O coração da Puramania é nosso elenco de estrelas. Acreditamos no poder da arte para unir, 
                celebrar e transformar. Convidamos você a construir o próximo grande espetáculo conosco.
              </p>
              
              {/* Métricas de Sucesso */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-white/10">
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-[#00F0FF]">+50</p>
                  <p className="text-sm text-gray-400">Eventos Realizados</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-[#FF0099]">+20</p>
                  <p className="text-sm text-gray-400">Artistas no Cast</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-[#00F0FF]">+100K</p>
                  <p className="text-sm text-gray-400">Pessoas Impactadas</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-[#FF0099]">+30</p>
                  <p className="text-sm text-gray-400">Cidades Atendidas</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Image Collage */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src="https://images.unsplash.com/photo-1768264312526-786bf6087043?w=600&q=80"
                  alt="Backstage"
                  className="rounded-xl w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1762788109986-8dcd959eeccb?w=600&q=80"
                  alt="Show"
                  className="rounded-xl w-full h-64 object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src="https://images.unsplash.com/photo-1766019463451-04e0b1269f37?w=600&q=80"
                  alt="Produção"
                  className="rounded-xl w-full h-64 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1762788109971-29b299b2b4ef?w=600&q=80"
                  alt="Evento"
                  className="rounded-xl w-full h-48 object-cover"
                />
              </div>
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-8 -right-8 w-32 h-32 border-2 border-[#FF0099]/30 rounded-xl -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    {
      image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop",
      title: "Gestão de Carreira Artística",
      description: "Desenvolvimento completo da carreira do artista, desde a identidade visual até a estratégia de mercado e negociações de contratos."
    },
    {
      image: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400&h=300&fit=crop",
      title: "Produção de Shows e Eventos",
      description: "Planejamento e execução de eventos de todos os portes, com equipe técnica especializada e logística impecável."
    },
    {
      image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=400&h=300&fit=crop",
      title: "Som e Iluminação Profissional",
      description: "Estrutura completa de áudio e luz de última geração, com técnicos experientes para garantir a melhor experiência."
    },
    {
      image: "https://images.unsplash.com/photo-1551818255-e6e10975bc17?w=400&h=300&fit=crop",
      title: "Planejamento Logístico",
      description: "Gestão de toda a logística do evento: transporte, hospedagem, alimentação, segurança e documentação."
    },
    {
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop&q=90",
      title: "Assessoria para Prefeituras",
      description: "Suporte completo para contratação por inexigibilidade de licitação, com toda documentação necessária em conformidade com a Lei 14.133/2021."
    },
    {
      image: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=400&h=300&fit=crop",
      title: "Marketing e Divulgação",
      description: "Estratégias de divulgação nas redes sociais, assessoria de imprensa e produção de material promocional."
    }
  ];

  return (
    <section id="servicos" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#00F0FF] text-sm font-mono tracking-[0.3em] uppercase mb-4 block">
            O Que Fazemos
          </span>
          <h2 className="section-title" data-testid="services-title">
            NOSSOS <span className="gradient-text">SERVIÇOS</span>
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Soluções completas para transformar seu evento em um sucesso absoluto
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-[#050505] rounded-xl border border-white/5 hover:border-[#00F0FF]/30 transition-all group overflow-hidden"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              </div>
              <div className="p-6 -mt-8 relative">
                <h3 className="font-unbounded font-bold text-white text-lg mb-3 group-hover:text-[#00F0FF] transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center mt-12"
        >
          <a href="#contato" className="btn-primary inline-flex items-center gap-2">
            Solicitar Orçamento
            <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const testimonials = [
    {
      name: "João Silva",
      role: "Secretário de Cultura",
      city: "Prefeitura Municipal",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop",
      text: "A Puramania superou todas as expectativas. O evento foi um sucesso absoluto, com organização impecável e um público estimado em mais de 15 mil pessoas. Recomendo fortemente!",
      rating: 5
    },
    {
      name: "Maria Santos",
      role: "Diretora de Eventos",
      city: "Empresa de Eventos",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop",
      text: "Profissionalismo do início ao fim. A equipe técnica é extremamente competente e a estrutura de som e luz entregue foi de altíssima qualidade. Parceria de sucesso!",
      rating: 5
    },
    {
      name: "Carlos Oliveira",
      role: "Produtor Cultural",
      city: "Festival Regional",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
      text: "Trabalhar com a Puramania é garantia de tranquilidade. Toda a documentação para contratação foi entregue corretamente e no prazo. Os artistas são excepcionais!",
      rating: 5
    }
  ];

  return (
    <section id="depoimentos" className="py-24 md:py-32 bg-[#050505]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#FF0099] text-sm font-mono tracking-[0.3em] uppercase mb-4 block">
            Cases de Sucesso
          </span>
          <h2 className="section-title" data-testid="testimonials-title">
            O QUE DIZEM <span className="gradient-text">NOSSOS PARCEIROS</span>
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            A confiança de quem já trabalhou conosco é nossa maior credencial
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="bg-[#0a0a0a] p-6 rounded-xl border border-white/5 relative"
            >
              {/* Quote icon */}
              <div className="absolute -top-4 left-6 text-5xl text-[#00F0FF]/20 font-serif">&ldquo;</div>
              
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Sparkles key={i} size={16} className="text-yellow-500 fill-yellow-500" />
                ))}
              </div>

              {/* Text */}
              <p className="text-gray-300 text-sm leading-relaxed mb-6 italic">
                &ldquo;{testimonial.text}&rdquo;
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#FF0099]"
                />
                <div>
                  <h4 className="font-bold text-white">{testimonial.name}</h4>
                  <p className="text-[#00F0FF] text-sm">{testimonial.role}</p>
                  <p className="text-gray-500 text-xs">{testimonial.city}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {[
            { number: "50+", label: "Eventos Realizados" },
            { number: "100k+", label: "Público Total" },
            { number: "20+", label: "Cidades Atendidas" },
            { number: "100%", label: "Clientes Satisfeitos" }
          ].map((stat, index) => (
            <div key={index} className="text-center p-6 bg-[#0a0a0a] rounded-xl border border-white/5">
              <div className="text-3xl md:text-4xl font-unbounded font-black gradient-text mb-2">
                {stat.number}
              </div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Artists Section
const ArtistsSection = ({ artists }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Helper: gerar slug do nome do artista (para URLs amigáveis)
  const artistSlug = (name) =>
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  // Deep linking: abrir modal do artista se URL tiver ?artista=slug
  useEffect(() => {
    if (!artists || artists.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const slug = params.get('artista');
    if (slug) {
      const artist = artists.find((a) => artistSlug(a.name) === slug);
      if (artist) setSelectedArtist(artist);
    }
  }, [artists]);

  // Atualizar URL quando modal abrir/fechar (sem recarregar)
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedArtist) {
      url.searchParams.set('artista', artistSlug(selectedArtist.name));
    } else {
      url.searchParams.delete('artista');
    }
    window.history.replaceState({}, '', url.toString());
  }, [selectedArtist]);

  // Construir link público para compartilhar (rota com Open Graph dinâmico do backend)
  // Quando colado em WhatsApp/Facebook, mostra preview com foto, nome e bio do artista.
  // Visitantes humanos são redirecionados ao modal do artista via meta-refresh.
  const buildShareUrl = (artist) => {
    const base = window.location.origin;
    return `${base}/api/share/${artistSlug(artist.name)}`;
  };

  // Copiar link para área de transferência
  const handleCopyLink = async (artist) => {
    const link = buildShareUrl(artist);
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Fallback para navegadores antigos
      const ta = document.createElement('textarea');
      ta.value = link;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2200);
  };

  // Texto pré-preenchido para WhatsApp
  const buildWhatsAppShareUrl = (artist) => {
    const link = buildShareUrl(artist);
    const text = `Confira o perfil de ${artist.name} no site da Puramania Produções: ${link}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  return (
    <section id="artistas" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="artists-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#00F0FF] text-sm font-mono tracking-[0.3em] uppercase mb-4 block">
            Nosso Elenco
          </span>
          <h2 className="section-title" data-testid="artists-title">
            CONHEÇA NOSSOS <span className="gradient-text">ARTISTAS</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {artists.map((artist, index) => (
            <motion.div
              key={artist.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`artist-card group cursor-pointer ${
                artist.featured ? "md:col-span-2 md:row-span-2" : ""
              }`}
              data-testid={`artist-card-${index}`}
              onClick={() => setSelectedArtist(artist)}
            >
              <div className={`relative overflow-hidden ${artist.featured ? "h-[500px]" : "h-80"}`}>
                <img
                  src={resolveImageUrl(artist.image)}
                  alt={artist.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block px-3 py-1 bg-[#FF0099] text-white text-xs font-bold uppercase tracking-wider rounded-full mb-3">
                    {artist.category}
                  </span>
                  <h3 className={`font-unbounded font-bold text-white ${
                    artist.featured ? "text-3xl" : "text-xl"
                  }`}>
                    {artist.name}
                  </h3>
                  <p className="text-[#00F0FF] text-sm mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    Clique para ver mais →
                  </p>
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-[#00F0FF]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal do Artista */}
      <AnimatePresence>
        {selectedArtist && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
            onClick={() => setSelectedArtist(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="w-full max-w-4xl my-auto bg-[#0a0a0a] rounded-2xl overflow-hidden border border-white/10 max-h-[95vh] sm:max-h-[90vh] flex flex-col md:grid md:grid-cols-2"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Foto do Artista */}
              <div className="relative w-full h-72 sm:h-80 md:h-auto md:min-h-[500px] flex-shrink-0">
                <img
                  src={resolveImageUrl(selectedArtist.image)}
                  alt={selectedArtist.name}
                  loading="eager"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:bg-gradient-to-r" />
                <button
                  onClick={() => setSelectedArtist(null)}
                  aria-label="Fechar"
                  className="absolute top-3 right-3 w-10 h-10 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-[#FF0099] transition-colors md:hidden z-10"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Informações do Artista */}
              <div className="p-5 sm:p-6 md:p-8 relative overflow-y-auto flex-1">
                <button
                  onClick={() => setSelectedArtist(null)}
                  aria-label="Fechar"
                  className="hidden md:flex absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full items-center justify-center text-white hover:bg-[#FF0099] transition-colors z-10"
                >
                  <X size={20} />
                </button>

                <span className="inline-block px-3 py-1 bg-[#FF0099] text-white text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                  {selectedArtist.category}
                </span>
                
                <h2 className="font-unbounded font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-5 md:mb-6 leading-tight">
                  {selectedArtist.name}
                </h2>

                  {/* Compartilhar Perfil — destaque no topo */}
                  <div className="mb-6 pb-6 border-b border-white/10">
                    <h3 className="text-[#00F0FF] font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Share2 size={16} />
                      Compartilhar Perfil
                    </h3>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      <a
                        href={buildWhatsAppShareUrl(selectedArtist)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`artist-share-whatsapp-${selectedArtist.id}`}
                        className="flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#1ebe57] rounded-lg text-white text-sm font-medium transition-colors"
                      >
                        <MessageCircle size={18} />
                        WhatsApp
                      </a>
                      <button
                        type="button"
                        onClick={() => handleCopyLink(selectedArtist)}
                        data-testid={`artist-share-copy-${selectedArtist.id}`}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${
                          copiedLink ? 'bg-emerald-600' : 'bg-zinc-700 hover:bg-zinc-600'
                        }`}
                      >
                        {copiedLink ? <Check size={18} /> : <Copy size={18} />}
                        {copiedLink ? 'Link copiado!' : 'Copiar link'}
                      </button>
                    </div>
                  </div>

                  {/* Contato da Produtora */}
                  <div className="mb-6">
                    <h3 className="text-[#00F0FF] font-bold text-sm uppercase tracking-wider mb-3">Contratação</h3>
                    <a
                      href="tel:+5571997081013"
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity w-fit"
                    >
                      <Phone size={18} />
                      (71) 99708-1013
                    </a>
                    <p className="text-gray-500 text-xs mt-2">Puramania Produções e Eventos</p>
                  </div>

                  {/* Redes Sociais */}
                  {selectedArtist.social_links && (selectedArtist.social_links.instagram || selectedArtist.social_links.facebook || selectedArtist.social_links.youtube) && (
                    <div className="mb-6">
                      <h3 className="text-[#00F0FF] font-bold text-sm uppercase tracking-wider mb-3">Redes Sociais</h3>
                      <div className="flex flex-wrap gap-2 sm:gap-3">
                        {selectedArtist.social_links.instagram && (
                          <a
                            href={selectedArtist.social_links.instagram.startsWith('http') ? selectedArtist.social_links.instagram : `https://www.instagram.com/${selectedArtist.social_links.instagram.replace('@', '').replace(/^\/+|\/+$/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`artist-social-instagram-${selectedArtist.id}`}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity"
                          >
                            <Instagram size={18} />
                            Instagram
                          </a>
                        )}
                        {selectedArtist.social_links.facebook && (
                          <a
                            href={selectedArtist.social_links.facebook.startsWith('http') ? selectedArtist.social_links.facebook : `https://www.facebook.com/${selectedArtist.social_links.facebook.replace(/^\/+|\/+$/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`artist-social-facebook-${selectedArtist.id}`}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity"
                          >
                            <Facebook size={18} />
                            Facebook
                          </a>
                        )}
                        {selectedArtist.social_links.youtube && (
                          <a
                            href={selectedArtist.social_links.youtube.startsWith('http') ? selectedArtist.social_links.youtube : `https://www.youtube.com/${selectedArtist.social_links.youtube.replace(/^\/+|\/+$/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`artist-social-youtube-${selectedArtist.id}`}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity"
                          >
                            <Youtube size={18} />
                            YouTube
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                {selectedArtist.description && (
                  <div className="mb-6">
                    <h3 className="text-[#00F0FF] font-bold text-sm uppercase tracking-wider mb-3">Biografia</h3>
                    <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                      {selectedArtist.description}
                    </p>
                  </div>
                )}

                  {/* Botão de Contato */}
                  <a
                    href="#contato"
                    onClick={() => setSelectedArtist(null)}
                    className="inline-flex items-center gap-2 btn-primary"
                  >
                    Contratar {selectedArtist.name.split(' ')[0]}
                    <ArrowRight size={18} />
                  </a>
                </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// News Section
const NewsSection = ({ news }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedNews, setSelectedNews] = useState(null);

  return (
    <section id="noticias" className="py-24 md:py-32 bg-[#050505]" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div>
            <span className="text-[#FF0099] text-sm font-mono tracking-[0.3em] uppercase mb-4 block">
              Fique por Dentro
            </span>
            <h2 className="section-title" data-testid="news-title">
              ÚLTIMAS <span className="gradient-text">NOTÍCIAS</span>
            </h2>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {news.map((item, index) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="news-card group cursor-pointer"
              data-testid={`news-card-${index}`}
              onClick={() => setSelectedNews(item)}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={resolveImageUrl(item.image)}
                  alt={item.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-[#FF0099] text-white text-xs font-bold uppercase tracking-wider rounded-full">
                    {item.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-unbounded font-bold text-xl text-white mb-3 line-clamp-2 group-hover:text-[#00F0FF] transition-colors">
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                  {item.excerpt}
                </p>
                <span className="text-[#00F0FF] font-medium text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                  Ler Notícia <ArrowRight size={14} />
                </span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Modal de Notícia */}
      <AnimatePresence>
        {selectedNews && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8"
            onClick={() => setSelectedNews(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="w-full max-w-4xl mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-[#0a0a0a] rounded-2xl overflow-hidden border border-white/10">
                {/* Header Image */}
                <div className="relative h-64 md:h-80">
                  <img
                    src={resolveImageUrl(selectedNews.image)}
                    alt={selectedNews.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                  <button
                    onClick={() => setSelectedNews(null)}
                    className="absolute top-4 right-4 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-[#FF0099] transition-colors"
                    data-testid="close-news-modal"
                  >
                    <X size={20} />
                  </button>
                  <div className="absolute bottom-4 left-6">
                    <span className="px-3 py-1 bg-[#FF0099] text-white text-xs font-bold uppercase tracking-wider rounded-full">
                      {selectedNews.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 md:p-10">
                  <h1 className="font-unbounded font-bold text-2xl md:text-3xl text-white mb-6">
                    {selectedNews.title}
                  </h1>
                  
                  <div className="prose prose-invert max-w-none">
                    {selectedNews.content && selectedNews.content.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="text-gray-300 text-base md:text-lg leading-relaxed mb-4 whitespace-pre-line">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Fonte */}
                  {selectedNews.source_url && (
                    <div className="mt-10 pt-8 border-t border-white/10">
                      <p className="text-gray-500 text-sm mb-4">
                        Fonte: <span className="text-[#00F0FF]">{selectedNews.source_name || 'Fonte Externa'}</span>
                      </p>
                      <a
                        href={selectedNews.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center gap-3"
                      >
                        Ler Matéria Completa <ArrowRight size={18} />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Gallery Section
const GallerySection = ({ gallery }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedItem, setSelectedItem] = useState(null);

  // Gerenciar overflow do body quando modal está aberto
  useEffect(() => {
    if (selectedItem) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedItem]);

  const openModal = (item) => {
    setSelectedItem(item);
  };

  const closeModal = () => {
    setSelectedItem(null);
  };

  // Extrair ID do YouTube da URL
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
    return match ? match[1] : null;
  };

  // Verificar se é um vídeo direto (mp4, webm, etc)
  const isDirectVideo = (url) => {
    if (!url) return false;
    return url.includes('/api/files/videos/') || 
           url.includes('/uploads/videos/') || 
           url.endsWith('.mp4') || 
           url.endsWith('.webm') || 
           url.endsWith('.mov');
  };

  return (
    <section id="galeria" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#00F0FF] text-sm font-mono tracking-[0.3em] uppercase mb-4 block">
            Nossos Momentos
          </span>
          <h2 className="section-title" data-testid="gallery-title">
            <span className="gradient-text">GALERIA</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {gallery.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className={`gallery-item cursor-pointer ${
                index === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              data-testid={`gallery-item-${index}`}
              onClick={() => openModal(item)}
            >
              <img src={resolveImageUrl(item.image)} alt={item.title} />
              {item.video_url && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Play size={28} className="text-white ml-1" fill="white" />
                  </div>
                </div>
              )}
              <div className="gallery-overlay flex items-end p-4">
                <div>
                  <span className="text-[#00F0FF] text-xs font-mono uppercase tracking-wider">
                    {item.category}
                  </span>
                  <h4 className="text-white font-bold mt-1">{item.title}</h4>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center mt-12"
        >
          <a
            href="https://www.instagram.com/puramaniaproducoes/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-3"
            data-testid="gallery-instagram-cta"
          >
            <Instagram size={20} />
            Siga no Instagram
          </a>
        </motion.div>
      </div>

      {/* Modal de Visualização - Formato igual ao de Artistas */}
      {selectedItem && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={closeModal}
        >
          <div 
            className="bg-[#0a0a0a] rounded-2xl overflow-hidden max-w-5xl w-full my-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Lado Esquerdo - Imagem/Vídeo */}
              <div className="relative bg-black">
                {selectedItem.video_url ? (
                  // Se tem vídeo
                  getYouTubeId(selectedItem.video_url) ? (
                    // YouTube embed
                    <div className="relative w-full h-full min-h-[300px] md:min-h-[500px]">
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${getYouTubeId(selectedItem.video_url)}?autoplay=1`}
                        title={selectedItem.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : isDirectVideo(selectedItem.video_url) ? (
                    // Vídeo direto (mp4, etc)
                    <video 
                      controls 
                      autoPlay
                      className="w-full h-full object-cover min-h-[300px] md:min-h-[500px]"
                    >
                      <source src={selectedItem.video_url} type="video/mp4" />
                      Seu navegador não suporta vídeo.
                    </video>
                  ) : (
                    // Instagram ou outro - mostrar imagem com overlay de play
                    <div className="relative">
                      <img 
                        src={resolveImageUrl(selectedItem.image)} 
                        alt={selectedItem.title}
                        className="w-full h-full object-cover min-h-[300px] md:min-h-[500px]"
                      />
                      <a
                        href={selectedItem.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
                      >
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
                          <Play size={36} className="text-white ml-1" fill="white" />
                        </div>
                      </a>
                    </div>
                  )
                ) : (
                  // Sem vídeo - só imagem
                  <img 
                    src={resolveImageUrl(selectedItem.image)} 
                    alt={selectedItem.title}
                    className="w-full h-full object-cover min-h-[300px] md:min-h-[500px]"
                  />
                )}
              </div>

              {/* Lado Direito - Informações */}
              <div className="p-6 md:p-8 relative">
                {/* Botão Fechar */}
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>

                {/* Categoria */}
                <span className="inline-block px-3 py-1 bg-gradient-to-r from-[#FF0099] to-[#FF0099] text-white text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                  {selectedItem.category}
                </span>

                {/* Título */}
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  {selectedItem.title}
                </h2>

                {/* Evento */}
                {selectedItem.event_name && (
                  <div className="mb-6">
                    <h3 className="text-[#00F0FF] font-bold text-sm uppercase tracking-wider mb-2">
                      Evento
                    </h3>
                    <p className="text-gray-300">{selectedItem.event_name}</p>
                  </div>
                )}

                {/* Descrição */}
                {selectedItem.description && (
                  <div className="mb-6">
                    <h3 className="text-[#00F0FF] font-bold text-sm uppercase tracking-wider mb-2">
                      Descrição
                    </h3>
                    <p className="text-gray-300 leading-relaxed">{selectedItem.description}</p>
                  </div>
                )}

                {/* Link da Fonte Original */}
                {selectedItem.source_url && (
                  <div className="mb-6">
                    <h3 className="text-[#00F0FF] font-bold text-sm uppercase tracking-wider mb-3">
                      Fonte Original
                    </h3>
                    <a
                      href={selectedItem.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#111] border border-white/10 rounded-full text-white hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
                    >
                      <Instagram size={18} />
                      Ver Publicação Original
                    </a>
                  </div>
                )}

                {/* Botão de Vídeo (se for link externo) */}
                {selectedItem.video_url && !getYouTubeId(selectedItem.video_url) && !isDirectVideo(selectedItem.video_url) && (
                  <a
                    href={selectedItem.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 w-full justify-center bg-gradient-to-r from-[#00F0FF] to-[#00F0FF] text-black font-bold px-6 py-4 rounded-full hover:opacity-90 transition-opacity mt-4"
                  >
                    <Play size={20} />
                    ASSISTIR VÍDEO
                    <ArrowRight size={18} />
                  </a>
                )}

                {/* Crédito */}
                <p className="text-gray-600 text-sm mt-6">
                  Puramania Produções e Eventos
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [artists, setArtists] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    event_type: "",
    contract_type: "",
    contract_details: "",
    selected_artists: [],
    event_date: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Buscar artistas disponíveis
  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const response = await axios.get(`${API}/artists`);
        setArtists(response.data);
      } catch (error) {
        console.error("Error fetching artists:", error);
      }
    };
    fetchArtists();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Converter array de artistas para string para enviar
      const dataToSend = {
        ...formData,
        selected_artists: formData.selected_artists.join(", ")
      };
      await axios.post(`${API}/contacts`, dataToSend);
      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        event_type: "",
        contract_type: "",
        contract_details: "",
        selected_artists: [],
        event_date: "",
      });
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleArtistToggle = (artistName) => {
    setFormData(prev => {
      const isSelected = prev.selected_artists.includes(artistName);
      if (isSelected) {
        return {
          ...prev,
          selected_artists: prev.selected_artists.filter(name => name !== artistName)
        };
      } else {
        return {
          ...prev,
          selected_artists: [...prev.selected_artists, artistName]
        };
      }
    });
  };

  // Placeholder dinâmico baseado no tipo de contratação
  const getContractDetailsPlaceholder = () => {
    switch(formData.contract_type) {
      case 'orgao_publico':
        return 'Ex: Prefeitura de Salvador, Secretaria de Cultura de Feira de Santana...';
      case 'festa_cidade':
        return 'Ex: São João de Caruaru, Natal de Natal, Carnaval de Salvador...';
      case 'festival_publico':
        return 'Ex: Festival de Inverno de Garanhuns, Micareta de Feira...';
      case 'evento_corporativo':
        return 'Ex: Nome da empresa, tipo de evento corporativo...';
      case 'festa_particular':
        return 'Ex: Aniversário, Casamento, Confraternização...';
      default:
        return 'Descreva os detalhes da contratação...';
    }
  };

  return (
    <section id="contato" className="py-24 md:py-32 bg-[#050505]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#FF0099] text-sm font-mono tracking-[0.3em] uppercase mb-4 block">
              Entre em Contato
            </span>
            <h2 className="section-title mb-8" data-testid="contact-title">
              FALE <span className="gradient-text">CONOSCO</span>
            </h2>
            <p className="text-gray-300 text-lg mb-12 leading-relaxed">
              Quer fazer um evento inesquecível? Entre em contato conosco e vamos 
              transformar sua ideia em realidade. Nossa equipe está pronta para atender você.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
                  <Mail className="text-[#00F0FF]" size={20} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Email</p>
                  <a href="mailto:puramaniaproducoes@gmail.com" className="text-white font-medium hover:text-[#00F0FF] transition-colors">
                    puramaniaproducoes@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#FF0099]/10 flex items-center justify-center">
                  <Phone className="text-[#FF0099]" size={20} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Telefone</p>
                  <a href="tel:+5571997081013" className="text-white font-medium hover:text-[#FF0099] transition-colors">
                    (71) 99708-1013
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
                  <MapPin className="text-[#00F0FF]" size={20} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Endereço</p>
                  <p className="text-white font-medium">André Guimarães Helitower - Sala 515</p>
                  <p className="text-gray-400 text-sm">Av. Santos Dumont, 6061 - Ao lado do Atacadão</p>
                  <p className="text-gray-400 text-sm">Lauro de Freitas/BA - CEP 42.712-740</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-12">
              <p className="text-gray-500 text-sm mb-4">Siga-nos</p>
              <div className="flex gap-4">
                {[
                  { icon: Instagram, href: "https://www.instagram.com/puramaniaproducoes/" },
                  { icon: Facebook, href: "https://www.facebook.com/puramaniaproducoes" },
                  { icon: Twitter, href: "https://twitter.com/puramania" },
                  { icon: Youtube, href: "https://www.youtube.com/@puramaniaproducoeseventos" },
                ].map(({ icon: Icon, href }, index) => (
                  <a
                    key={index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-[#00F0FF] hover:bg-[#00F0FF]/10 transition-all"
                    data-testid={`social-link-${index}`}
                  >
                    <Icon size={20} />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form
              onSubmit={handleSubmit}
              className="bg-[#0a0a0a] p-8 rounded-2xl border border-white/5"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Nome *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Seu nome"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="seu@email.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Telefone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="(00) 00000-0000"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Tipo de Contratação *</label>
                  <select
                    name="contract_type"
                    value={formData.contract_type}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="input-contract-type"
                  >
                    <option value="">Selecione o tipo de contratação...</option>
                    <option value="orgao_publico">Órgão Público / Prefeitura</option>
                    <option value="festa_cidade">Festa da Cidade (São João, Natal, Carnaval, etc.)</option>
                    <option value="festival_publico">Festival Público</option>
                    <option value="evento_corporativo">Evento Corporativo / Empresa Privada</option>
                    <option value="festa_particular">Festa Particular</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
              </div>

              {/* Campo de detalhes da contratação - aparece após selecionar o tipo */}
              {formData.contract_type && (
                <div className="mb-6">
                  <label className="block text-gray-400 text-sm mb-2">
                    {formData.contract_type === 'orgao_publico' && 'Qual Prefeitura/Órgão Público? *'}
                    {formData.contract_type === 'festa_cidade' && 'Qual Festa e Cidade? *'}
                    {formData.contract_type === 'festival_publico' && 'Qual Festival? *'}
                    {formData.contract_type === 'evento_corporativo' && 'Qual Empresa/Evento? *'}
                    {formData.contract_type === 'festa_particular' && 'Detalhes da Festa *'}
                    {formData.contract_type === 'outro' && 'Descreva o tipo de contratação *'}
                  </label>
                  <input
                    type="text"
                    name="contract_details"
                    value={formData.contract_details}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder={getContractDetailsPlaceholder()}
                    data-testid="input-contract-details"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Tipo de Evento</label>
                  <select
                    name="event_type"
                    value={formData.event_type}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="input-event-type"
                  >
                    <option value="">Selecione...</option>
                    <option value="show">Show</option>
                    <option value="festival">Festival</option>
                    <option value="corporativo">Evento Corporativo</option>
                    <option value="casamento">Casamento</option>
                    <option value="formatura">Formatura</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Data do Evento</label>
                  <input
                    type="date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="input-event-date"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2">Assunto *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Assunto da mensagem"
                  data-testid="input-subject"
                />
              </div>

              <div className="mb-8">
                <label className="block text-gray-400 text-sm mb-2">Mensagem *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="form-input resize-none"
                  placeholder="Conte-nos mais sobre seu evento..."
                  data-testid="input-message"
                />
              </div>

              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-center"
                  data-testid="success-message"
                >
                  Mensagem enviada com sucesso! Entraremos em contato em breve.
                </motion.div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-3"
                data-testid="submit-button"
              >
                {loading ? (
                  "Enviando..."
                ) : (
                  <>
                    Enviar Mensagem <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#050505] border-t border-white/5 relative" data-testid="footer">
      {/* Site Seguro Badge - Fixed bottom right */}
      <div className="fixed bottom-3 right-3" style={{ zIndex: 2147483647 }}>
        <div className="bg-green-600 text-white px-3 py-2 rounded-lg shadow-xl flex items-center gap-2 text-sm font-medium border border-green-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
          <div className="flex flex-col leading-tight">
            <span className="font-bold text-xs">SITE SEGURO</span>
            <span className="text-[10px] opacity-90">🔒 SSL/TLS Ativo</span>
          </div>
        </div>
      </div>
      {/* Large Brand Text */}
      <div className="py-16 overflow-hidden">
        <h2 className="footer-brand text-center">PURAMANIA</h2>
      </div>

      {/* Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_event-puramania/artifacts/agcwure7_logo%20marca%202.png" 
              alt="Puramania Logo" 
              className="h-16 w-auto mb-4"
            />
            <p className="text-gray-400 mt-4 max-w-md">
              Excelência em Produção e Eventos<br/>
              Shows, festas, Estrutura e criatividade
            </p>
          </div>

          <div>
            <h4 className="font-unbounded font-bold text-white mb-4">Links Rápidos</h4>
            <ul className="space-y-3">
              <li>
                <a href="#sobre" className="text-gray-400 hover:text-white transition-colors">Sobre Nós</a>
              </li>
              <li>
                <a href="#servicos" className="text-gray-400 hover:text-white transition-colors">Serviços</a>
              </li>
              <li>
                <a href="#depoimentos" className="text-gray-400 hover:text-white transition-colors">Depoimentos</a>
              </li>
              <li>
                <a href="#artistas" className="text-gray-400 hover:text-white transition-colors">Artistas</a>
              </li>
              <li>
                <a href="#noticias" className="text-gray-400 hover:text-white transition-colors">Notícias</a>
              </li>
              <li>
                <a href="#contato" className="text-gray-400 hover:text-white transition-colors">Contato</a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-unbounded font-bold text-white mb-4">Contato</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-[#00F0FF]" />
                puramaniaproducoes@gmail.com
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-[#FF0099]" />
                (71) 99708-1013
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="text-green-500 mt-1" />
                <span>
                  André Guimarães Helitower - Sala 515<br/>
                  Av. Santos Dumont, 6061<br/>
                  Lauro de Freitas/BA - CEP 42.712-740
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Company Info Bar */}
        <div className="mt-8 p-4 bg-[#0a0a0a] rounded-lg border border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <div className="text-gray-400">
              <strong className="text-white">Razão Social:</strong> PURAMANIA PRODUÇÕES E EVENTOS LTDA
            </div>
            <div className="text-gray-400">
              <strong className="text-white">CNPJ:</strong> 52.249.538/0001-38
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Puramania Produções e Eventos LTDA. Todos os direitos reservados.
          </p>
          <div className="flex gap-6">
            {[
              { icon: Instagram, href: "https://www.instagram.com/puramaniaproducoes/" },
              { icon: Facebook, href: "https://www.facebook.com/puramaniaproducoes" },
              { icon: Twitter, href: "https://twitter.com/puramania" },
              { icon: Youtube, href: "https://www.youtube.com/@puramaniaproducoeseventos" },
            ].map(({ icon: Icon, href }, index) => (
              <a
                key={index}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-[#00F0FF] transition-colors"
              >
                <Icon size={20} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Website Component
const MainWebsite = () => {
  const [artists, setArtists] = useState([]);
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        
        // Fetch all data
        const [artistsRes, newsRes, galleryRes] = await Promise.all([
          axios.get(`${API}/artists`),
          axios.get(`${API}/news`),
          axios.get(`${API}/gallery`),
        ]);
        
        setArtists(artistsRes.data);
        setNews(newsRes.data);
        setGallery(galleryRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };

    initializeData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="loading-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App bg-[#050505] min-h-screen" data-testid="app-container">
      <Navigation />
      <HeroSection />
      <MarqueeSection />
      <AboutSection />
      <ServicesSection />
      <TestimonialsSection />
      <ArtistsSection artists={artists} />
      <NewsSection news={news} />
      <GallerySection gallery={gallery} />
      <ContactSection />
      <Footer />
    </div>
  );
};

// Main App Component with Routes
function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public Website */}
        <Route path="/" element={<MainWebsite />} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<LoginPage />} />
        <Route path="/admin/*" element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="artistas" element={<ArtistsPage />} />
          <Route path="noticias" element={<NewsPage />} />
          <Route path="galeria" element={<GalleryPage />} />
          <Route path="contatos" element={<ContactsPage />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

export default App;
